import {
    IInitialState,
    UpsellItemFromStorage,
} from './interfaces/state';
import {
    AbstractedUpsellCategory,
    AbstractedUpsellItem,
    isTariffWithHardwareResponse,
    isUpsellItem,
    PriceType,
    UpsellFacetValue,
} from '@vfde-sails/glados-v2';
import {
    asyncThunkCreator,
    buildCreateSlice,
    isAnyOf,
    original,
} from '@reduxjs/toolkit';
import { CustomerConfig } from 'Helper/getCustomerConfig';
import {
    getDeviceDetailAtomic,
    getDeviceDetailGroup,
    getProducts,
    getSimOnly,
    getTariffWithHardware,
} from './api/glados';
import { RootState } from '../../store';
import {
    ConnectMoreError,
    ConnectMoreErrorType,
} from './interfaces/error';
import { toggleArrayItem } from '@vfde-sails/utils';

/**
 * Initial state
 */
const initialState: IInitialState = {
    atomicId: null,
    deviceId: null,
    salesChannel: null,
    selectedCategories: [],
    selectedProducts: [],
    subscriptionId: null,
    atomicTariffId: null,
    discounts: [],
    insurance: null,
    redPlusIds: [],
    externalDiscounts: {
        [PriceType.Monthly]: 0,
        [PriceType.Onetime]: 0,
    },
    error: null,
};

const createAppSlice = buildCreateSlice({
    creators: { asyncThunk: asyncThunkCreator },
});

const appSlice = createAppSlice({
    name: 'ConnectMore',
    initialState,
    reducers: create => {
        const createAThunk = create.asyncThunk.withTypes<{
            rejectValue: ConnectMoreError;
        }>();

        return {
            setInitialState: createAThunk<Omit<CustomerConfig, 'btx'> & {
                categories: AbstractedUpsellCategory[];
                products: AbstractedUpsellItem[];
            }, {
                customerConfig: Omit<CustomerConfig, 'btx'>,
                categories: AbstractedUpsellCategory[],
                products: AbstractedUpsellItem[],
            }>(
                (arg, thunkAPI) => {
                    const { atomicId, deviceId, subscriptionId } = arg.customerConfig;

                    if (!atomicId && !deviceId && !subscriptionId) {
                        throw thunkAPI.rejectWithValue({
                            type: ConnectMoreErrorType.NoProducts,
                            message: 'No products selected',
                        });
                    }

                    return {
                        ...arg.customerConfig,
                        categories: arg.categories,
                        products: arg.products,
                    };
                },
                {
                    fulfilled: (state, action) => {
                        state.atomicId = action.payload.atomicId || null;
                        state.deviceId = action.payload.deviceId || null;
                        state.subscriptionId = action.payload.subscriptionId || null;
                        state.salesChannel = action.payload.salesChannel;
                        state.discounts = action.payload.discounts;
                        state.insurance = action.payload.insurance;
                        state.redPlusIds = action.payload.redPlusIds;
                        state.selectedProducts = action.payload.products;

                        // Sim only
                        if (action.payload.subscriptionId && !action.payload.deviceId && !action.payload.atomicId) {
                            state.selectedCategories = [{ value: UpsellFacetValue.Redplus }] as AbstractedUpsellCategory[];
                        }
                        // Hardware only
                        else if (!action.payload.subscriptionId) {
                            state.selectedCategories = [{ value: UpsellFacetValue.Accessory }] as AbstractedUpsellCategory[];
                        }
                        else {
                            state.selectedCategories = action.payload.categories;
                        }
                    },
                    rejected: (state, action) => {
                        state.error = action.payload || null;
                    },
                },
            ),
            toggleCategory: create.reducer<AbstractedUpsellCategory>((state, action) => {
                state.selectedCategories = toggleArrayItem<AbstractedUpsellCategory>(
                    original(state)?.selectedCategories || [],
                    action.payload,
                    category => category.value === action.payload.value,
                );
            }),
            toggleProduct: create.reducer<AbstractedUpsellItem>((state, action) => {
                state.selectedProducts = toggleArrayItem<UpsellItemFromStorage>(
                    original(state)?.selectedProducts || [],
                    action.payload,
                    product => product.id === action.payload.id,
                );
                state.insurance = state.selectedProducts
                    .find(product => isUpsellItem(product) && product.category.value === UpsellFacetValue.Insurance)?.id || null
                ;
                state.redPlusIds = state.selectedProducts
                    .filter(product => isUpsellItem(product) && product.category.value === UpsellFacetValue.Redplus)
                    .map(product => product.id)
                ;
                state.error = null;
            }),
            setCategories: create.reducer<AbstractedUpsellCategory[]>((state, action) => {
                state.selectedCategories = action.payload;
            }),
            setProducts: create.reducer<AbstractedUpsellItem[]>((state, action) => {
                state.selectedProducts = action.payload;
                state.insurance = state.selectedProducts
                    .find(product => isUpsellItem(product) && product.category.value === UpsellFacetValue.Insurance)?.id || null;
                state.redPlusIds = state.selectedProducts
                    .filter(product => isUpsellItem(product) && product.category.value === UpsellFacetValue.Redplus)
                    .map(product => product.id)
                ;
            }),
            addMonthlyDiscount: create.reducer<number>((state, action) => {
                state.externalDiscounts[PriceType.Monthly] = +action.payload || 0;
            }),
            addOneTimeDiscount: create.reducer<number>((state, action) => {
                state.externalDiscounts[PriceType.Onetime] = +action.payload || 0;
            }),
            goToBasket: createAThunk<Pick<
                IInitialState,
                'selectedProducts' | 'salesChannel' | 'atomicTariffId' | 'atomicId' | 'discounts'
            >, void>(
                (
                    _arg,
                    thunkAPI,
                ): Pick<IInitialState, 'selectedProducts' | 'salesChannel' | 'atomicTariffId' | 'atomicId' | 'discounts'> => {
                    const state = thunkAPI.getState() as RootState;

                    const [
                        selectedProducts,
                        atomicId,
                        atomicTariffId,
                        salesChannel,
                        discounts,
                    ] = [
                        selectSelectedProducts(state),
                        selectAtomicId(state),
                        selectAtomicTariffId(state),
                        selectSalesChannel(state),
                        selectDiscounts(state),
                    ];

                    const valid = (selectedProducts
                        ?.filter(product => product.category?.value === UpsellFacetValue.Redplus)?.length || 0) < 3;

                    if (!valid) {
                        throw thunkAPI.rejectWithValue({
                            type: ConnectMoreErrorType.TooManyRedPlus,
                            message: 'Too many Red+ cards',
                        });
                    }

                    return {
                        selectedProducts,
                        atomicId,
                        atomicTariffId,
                        salesChannel,
                        discounts,
                    };
                },
                {
                    rejected: (state, action) => {
                        state.error = action.payload || null;
                    },
                },
            ),
        };
    },
    selectors: {
        selectSalesChannel: (sliceState): IInitialState['salesChannel'] => sliceState.salesChannel,
        selectSubscriptionId: (sliceState): IInitialState['subscriptionId'] => sliceState.subscriptionId,
        selectAtomicTariffId: (sliceState): IInitialState['atomicTariffId'] => sliceState.atomicTariffId,
        selectAtomicId: (sliceState): IInitialState['atomicId'] => sliceState.atomicId,
        selectDeviceId: (sliceState): IInitialState['deviceId'] => sliceState.deviceId,
        selectSelectedProducts: (sliceState: IInitialState): IInitialState['selectedProducts'] => sliceState.selectedProducts,
        selectSelectedCategories: (sliceState): IInitialState['selectedCategories'] => sliceState.selectedCategories,
        selectDiscounts: (sliceState): IInitialState['discounts'] => sliceState.discounts,
        selectInsurance: (sliceState): IInitialState['insurance'] => sliceState.insurance,
        selectRedPlusIds: (sliceState): IInitialState['redPlusIds'] => sliceState.redPlusIds,
        selectSimOnly: (sliceState): boolean => !!sliceState.subscriptionId && !sliceState.atomicId && !sliceState.deviceId,
        selectHardwareOnly: (sliceState): boolean => !sliceState.subscriptionId && !!(sliceState.deviceId || sliceState.atomicId),
        selectExternalDiscounts: (sliceState): IInitialState['externalDiscounts'] => sliceState.externalDiscounts,
        selectError: (sliceState): IInitialState['error'] => sliceState.error,
        selectHasError: (sliceState): boolean => !!sliceState.error,
    },
    extraReducers: builder => {
        builder
            .addMatcher(
                getProducts.matchFulfilled,
                (state, action) => {
                    const { categories: availableCategories, items } = action.payload;

                    if (availableCategories.length > 0) {
                        state.selectedCategories = original(state)?.selectedCategories
                            .map(selectedCategory => availableCategories
                                .find(availableCategory => availableCategory.value === selectedCategory.value)
                                || selectedCategory,
                            ) || [];
                    }

                    if (items.length > 0) {
                        state.selectedProducts = original(state)?.selectedProducts
                            ?.map(selectedProduct => items
                                .find(product => product.id === selectedProduct.id)
                                || selectedProduct,
                            ) || [];
                    }
                },
            )
            .addMatcher(
                getDeviceDetailGroup.matchFulfilled,
                (state, action) => {
                    state.atomicId = action.payload.atomics.find(atomicDevice => atomicDevice.isDefault)!.atomicId;
                },
            )
            .addMatcher(
                isAnyOf(getSimOnly.matchFulfilled, getTariffWithHardware.matchFulfilled),
                (state, action) => {
                    if (isTariffWithHardwareResponse(action.payload)) {
                        state.atomicTariffId = action.payload
                            .find(offer => offer.hardware.atomicId === original(state)?.atomicId)
                            ?.tariffs
                            .find(tariff => tariff.tariffId === original(state)?.subscriptionId)?.subLevelId || null;
                    }
                    else {
                        state.atomicTariffId = action.payload.tariffs
                            .find(tariff => tariff.tariffId === original(state)?.subscriptionId)?.subLevelId || null;
                    }
                },
            )
            .addMatcher(
                isAnyOf(
                    getProducts.matchRejected,
                    getSimOnly.matchRejected,
                    getTariffWithHardware.matchRejected,
                    getDeviceDetailGroup.matchRejected,
                    getDeviceDetailAtomic.matchRejected,
                ),
                (state, action) => {
                    state.error = {
                        type: ConnectMoreErrorType.General,
                        message: action.error.message || 'Unknown error',
                    };
                },
            )
        ;
    },
});

export default appSlice;

export const {
    setInitialState,
    toggleCategory,
    toggleProduct,
    setCategories,
    setProducts,
    addMonthlyDiscount,
    addOneTimeDiscount,
    goToBasket,
} = appSlice.actions;
export const {
    selectSalesChannel,
    selectSubscriptionId,
    selectAtomicId,
    selectDeviceId,
    selectSelectedProducts,
    selectSelectedCategories,
    selectDiscounts,
    selectInsurance,
    selectRedPlusIds,
    selectSimOnly,
    selectHardwareOnly,
    selectAtomicTariffId,
    selectExternalDiscounts,
    selectError,
    selectHasError,
} = appSlice.selectors;
